'use client';

import { CheckIcon } from 'lucide-react';
import * as React from 'react';

import { Checkbox } from 'src/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from 'src/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { useControl } from 'src/hooks';
import { cn } from 'src/lib/utils';

import { FilterButton } from './filter-button';

type FilterValueType<TMultiple extends boolean, V> = (TMultiple extends true ? V[] : V) | undefined;

interface FacetedFilterProps<TMultiple extends boolean = false, V = any> {
  title: string;
  searchPlaceholder?: string;
  loading?: boolean;
  loadingText?: string;
  multiple?: TMultiple;
  options: {
    label: string;
    value: any;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  className?: string;

  value?: FilterValueType<TMultiple, V>;
  onChange?: (value?: FilterValueType<TMultiple, V>) => void;
  defaultValue?: FilterValueType<TMultiple, V>;
  facets?: Map<any, number>;
  onReset?: () => void;
}

export function FacetedFilter<TMultiple extends boolean = false, V = any>({
  title,
  searchPlaceholder,
  loading = false,
  loadingText = '加载中...',
  multiple,
  options,
  className,
  facets,
  defaultValue,
  value,
  onChange,
  onReset,
}: FacetedFilterProps<TMultiple, V>) {
  const [open, setOpen] = React.useState(false);
  const [selection, setSelection] = useControl<FilterValueType<TMultiple, V>>(
    defaultValue,
    value,
    onChange
  );

  const selectedValues = Array.isArray(selection)
    ? selection
    : selection !== undefined
      ? [selection]
      : [];
  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.value))
    .map((option) => option.label);

  const handleReset = () => {
    setSelection(multiple ? ([] as FilterValueType<TMultiple, V>) : undefined);
    setOpen(false); // 重置时关闭下拉框
    onReset?.();
  };

  const handleOptionSelect = (optionValue: any) => {
    let value: FilterValueType<TMultiple, V>;
    if (multiple) {
      // 多选模式
      const newSelected = [...selectedValues];
      const index = newSelected.indexOf(optionValue);

      if (index > -1) {
        newSelected.splice(index, 1);
      } else {
        newSelected.push(optionValue);
      }
      value = (newSelected.length ? newSelected : []) as FilterValueType<TMultiple, V>;
    } else {
      // 单选模式
      const isSameValue = selectedValues[0] === optionValue;

      value = isSameValue ? undefined : (optionValue as FilterValueType<TMultiple, V>);

      // 选中后自动关闭下拉框
      if (!isSameValue) {
        setOpen(false);
      }
    }
    setSelection(value);
    onChange?.(value);
  };
  const emptyText = options.length > 0 ? '未找到匹配项' : '暂无数据';
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FilterButton title={title} selectedLabels={selectedLabels} />
      </PopoverTrigger>
      <PopoverContent className={cn('w-[200px] p-0', className)} align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder || title} />
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <span className="text-sm text-muted-foreground">{loadingText}</span>
            </div>
          ) : (
            <>
              <CommandList className="max-h-60 overflow-y-auto">
                <CommandEmpty>{emptyText}</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <CommandItem
                        aria-selected={isSelected}
                        key={option.value}
                        onSelect={() => handleOptionSelect(option.value)}
                      >
                        {multiple ? (
                          <Checkbox
                            checked={isSelected}
                            className="[&[data-state=checked]_svg]:text-primary-foreground"
                          />
                        ) : isSelected ? (
                          <CheckIcon className="h-4 w-4" />
                        ) : (
                          <span className="h-4 w-4" />
                        )}
                        {option.icon && (
                          <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="truncate inline-block align-middle" title={option.label}>
                          {option.label}
                        </span>
                        {!!facets && (
                          <span className="ml-auto font-mono text-xs">
                            {facets?.get(option.value) || 0}
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  disabled={selectedValues.length === 0}
                  onSelect={handleReset}
                  className="justify-center text-center cursor-pointer"
                >
                  重 置
                </CommandItem>
              </CommandGroup>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
